import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Building2 } from 'lucide-react';
import { useSeo } from '@/lib/seo';

const GROUPS = [
  {
    heading: 'Introduction',
    items: [
      { n: 1, body: ['This privacy policy sets out how Motion Reality Sim Racing Ltd uses and protects information that you give when you use this website.'] },
      { n: 2, body: ['We are committed to ensuring that your privacy is protected at all times. Any information that you provide will be handled in accordance with this privacy policy.'] },
      { n: 3, body: ['We may change this policy from time to time by updating this page and so you should check it regularly to ensure that you are happy with any changes.'] },
    ],
  },
  {
    heading: 'Personal Information We Collect When You Visit Our Website',
    items: [
      { n: 4, body: ['When you visit our website, we\'ll automatically collect certain information about the device you are using to access it. This will include information about the website browser you are using and the IP address of your internet connection. We may also identify your device with a unique, randomly generated, identifier.'] },
      { n: 5, body: ['As you browse our website we\'ll continue to collect information about the website pages that you look at during your visit, how long you visit them, and what links you click on each page. We may also record what keywords you used to locate our website and what third-party website referred you to our website.'] },
      { n: 6, body: ['The information we collect about you is referred to as Device Information. It\'s not personally identifiable to you and it\'s not stored on our systems for any longer than we need to store it in order to allow you to use our website for the purposes in which it is intended.'] },
      { n: 7, body: ['We collect Device Information from you using Cookies. These are small data files that are placed on the browser you use, on your device, to visit the website. Cookies often include an anonymous and unique identifier.'] },
      { n: 8, body: [
        'Certain cookies are placed on our website in order to make it work. For example, when you add items to the shopping basket/cart we need to store this information on a cookie. Where cookies we place on your computer are:',
      ], list: [
        'Not identifiable to you as a living person, and',
        'Only placed in order to fulfil the inherent functions of an ecommerce website',
      ], after: ['we won\'t ask for your permission before we place them.'] },
      { n: 9, body: ['We may, from time to time, use cookies placed by third party companies to track and monitor you as you visit the website. Sometimes, these third party cookies may include information which might, indirectly, identify you as a living person. Where we do so, we\'ll always ask for your consent before we place those cookies.'] },
      { n: 10, body: ['We may, from time to time, also collect information about your actions on the website and record those actions in log files. This data will include your IP address and may include the device that you are using, the browser that you are using, your internet service provider and be attached to date and time stamps. We\'ll only include this information for as long as necessary.'] },
    ],
  },
  {
    heading: 'Personal Information We Collect When You Make a Purchase or Register for an Account',
    items: [
      { n: 11, body: ['When you use our website to make a purchase of one of the goods or services that we offer for sale, or when you register for an account on our website (without making a purchase) you may be asked to provide certain personal information about you.'] },
      { n: 12, body: ['Such information we collect may include:'], list: [
        'Your name',
        'Your address',
        'Your contact information (like your telephone number or email address)',
        'Your date of birth',
      ] },
      { n: 13, body: ['When you make a purchase on our website you may also be asked to enter your credit or debit card details. You may be redirected to a \'payment gateway\' website to enter your card details, or you might be asked to enter these directly onto our website.'] },
      { n: 14, body: [
        'Your full credit or debit card details are never stored on any databases we hold. You may be asked if you want to \'store\' your purchase information for faster checkout at a later date. Where you do store this information the full card information is never stored on any databases we hold. Instead we send the full information to our payment processor, who then send back a secure \'token\' which we then use to place future purchases. To help you identify stored cards, we may store the last 4 digits of the card, the type of card (e.g. Visa or MasterCard) and the expiry date.',
        'Where you wish, you can always delete a stored card within your customer account.',
      ] },
      { n: 15, body: ['We call this data Personal Information.'] },
    ],
  },
  {
    heading: 'How We Use Personal Information and Device Information',
    items: [
      { n: 16, body: ['We use Device Information we collect in order to help us screen for potential risk and fraud, to improve and optimise our website and to analyse the success of our marketing and other advertising campaigns.'] },
      { n: 17, body: ['We use Personal Information that we collect to fulfil any order that you place and to communicate with you as one of our customers. We may also use this information to screen for potential risk and fraud.'] },
      { n: 18, body: ['With your consent, we may, from time to time, provide you with information or advertising relating to the products and services that we provide.'] },
    ],
  },
  {
    heading: 'Sharing Your Personal Information and Device Information',
    items: [
      { n: 19, body: ['We will share personal information and device information with third parties in their capacity as a data processor on our behalf. We\'ll only share your information with third parties in so far as they assist us with using your information.'] },
      { n: 20, body: ['We may also share your personal information and device information in order to comply with applicable laws and regulations or to respond to any lawful request for information we receive, or to otherwise protect our rights.'] },
    ],
  },
  {
    heading: 'Re-marketing and Behavioural Advertising',
    items: [
      { n: 21, body: ['We may, from time to time, share your device or personal information to provide you with targeted advertisements or marketing communications we believe might be of interest to you. This may include sharing some of your information with third party networks such as Google, Bing or Facebook.'] },
    ],
  },
  {
    heading: 'Do Not Track',
    items: [
      { n: 22, body: ['Our website does not alter its data collection and use practices when we see a Do Not Track signal from your browser.'] },
    ],
  },
  {
    heading: 'GDPR',
    items: [
      { n: 23, body: ['If you are a resident of a country where the European Union\'s GDPR is applicable, you have certain rights.'] },
      { n: 24, body: ['To exercise your rights to access your data, receive a copy of that data, or to rectify a mistake, please contact us.'] },
    ],
  },
  {
    heading: 'General Terms',
    items: [
      { n: 25, body: ['This privacy policy may be updated from time to time, in order to reflect changes to our business practices or for other operational or legal reasons.'] },
      { n: 26, body: ['For more information please contact us using the details below.'] },
    ],
  },
];

const COMPANY = {
  name: 'Motion Reality Sim Racing Ltd',
  reg: '16171282',
  email: 'sales@motionreality.co.uk',
  phone: '0151 528 3001',
};

export default function PrivacyPolicy() {
  useSeo({
    title: 'Privacy Policy — Motion Reality Sim Racing Ltd',
    description: 'How Motion Reality Sim Racing Ltd collects, uses and protects your personal and device information when you use our website.',
  });

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-16 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-primary text-xs font-bold tracking-widest mb-3">LEGAL</p>
            <h1 className="text-white font-black text-4xl md:text-5xl uppercase leading-tight mb-3">
              Privacy Policy
            </h1>
            <p className="text-white/40 text-sm">
              Last updated: August 2026 · This policy applies to all use of motionreality.co.uk.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact card */}
      <section className="px-6 lg:px-10 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#111115] border border-white/5 p-7">
            <div className="flex items-center gap-2 mb-5">
              <Building2 className="w-4 h-4 text-primary" />
              <p className="text-primary text-[10px] font-bold tracking-widest">CONTACT US</p>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div><dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">COMPANY</dt><dd className="text-white/80">{COMPANY.name}</dd></div>
              <div><dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">REGISTRATION NUMBER</dt><dd className="text-white/80">{COMPANY.reg}</dd></div>
              <div>
                <dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">EMAIL</dt>
                <dd><a href={`mailto:${COMPANY.email}`} className="text-primary hover:text-primary/80 inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{COMPANY.email}</a></dd>
              </div>
              <div>
                <dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">TELEPHONE</dt>
                <dd><a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="text-primary hover:text-primary/80 inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{COMPANY.phone}</a></dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="px-6 lg:px-10 pb-20">
        <div className="max-w-3xl mx-auto space-y-12">
          {GROUPS.map((group) => (
            <motion.div
              key={group.heading}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-8 h-[3px] bg-primary mb-4" />
              <h2 className="text-white font-black text-lg uppercase tracking-wide mb-6">{group.heading}</h2>
              <div className="space-y-8">
                {group.items.map((s) => (
                  <div key={s.n}>
                    <div className="flex items-baseline gap-3 mb-3">
                      <span className="text-primary font-mono font-bold text-sm">{String(s.n).padStart(2, '0')}</span>
                      <div className="space-y-3 flex-1">
                        {s.body.map((p, i) => (
                          <p key={i} className="text-white/55 text-sm leading-relaxed">{p}</p>
                        ))}
                        {s.list && (
                          <ul className="space-y-2 mt-1">
                            {s.list.map((item, i) => (
                              <li key={i} className="flex items-start gap-2.5 text-white/55 text-sm leading-relaxed">
                                <span className="text-primary mt-1.5 shrink-0">•</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {s.after?.map((p, i) => (
                          <p key={`a${i}`} className="text-white/55 text-sm leading-relaxed">{p}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}